import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { getQuestionsForQuestionnaire, getQuestionnaireType } from '../../utils/questionnaireTypes';
import { v4 as uuidv4 } from 'uuid';

const QUESTIONS_PER_PAGE = 8;

const BaseQuestionnaire = ({ children, onQuestionChange, onNavigate }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user, loading: authLoading } = useAuth();

  const [questions, setQuestions] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [questionnaireConfig, setQuestionnaireConfig] = useState(null);

  // Anonymous user ID
  const [anonymousId, setAnonymousId] = useState(null);

  const totalPages = Math.ceil(questions.length / QUESTIONS_PER_PAGE);
  const currentQuestions = questions.slice(
    currentPage * QUESTIONS_PER_PAGE,
    (currentPage + 1) * QUESTIONS_PER_PAGE
  );
  const progress = totalPages > 0 ? ((Object.keys(answers).length) / questions.length) * 100 : 0;

  const loadProgress = useCallback(async (currentUserId) => {
    if (!currentUserId) return;

    try {
      const apiUrl =
        process.env.NODE_ENV === 'development'
          ? 'http://localhost:7071/api/loadProgress'
          : '/api/loadProgress';

      const response = await axios.get(apiUrl, {
        params: { userId: currentUserId, questionnaireId: id },
        withCredentials: true,
      });

      if (response.data.progress) {
        const { progress } = response.data;
        setAnswers(progress.answers || {});
        setCurrentPage(progress.currentPage || 0);
      }
    } catch (error) {
      console.error('Erreur lors du chargement de la progression:', error);
    }
  }, [id]);

  useEffect(() => {
    if (!isAuthenticated) {
      let anId = localStorage.getItem('anonymousId');
      if (!anId) {
        anId = uuidv4();
        localStorage.setItem('anonymousId', anId);
      }
      setAnonymousId(anId);
    }
  }, [isAuthenticated]);

  const initializeQuestionnaire = useCallback(async () => {
    try {
      // Get questionnaire configuration
      const config = getQuestionnaireType(id);
      if (!config) {
        throw new Error(`Configuration not found for questionnaire: ${id}`);
      }
      setQuestionnaireConfig(config);

      // Load questions for this questionnaire type
      const loadedQuestions = getQuestionsForQuestionnaire(id);
      setQuestions(loadedQuestions);

      const currentUserId = isAuthenticated ? user?.id : localStorage.getItem('anonymousId');
      if (currentUserId) {
        await loadProgress(currentUserId);
      }
    } catch (error) {
      console.error('Erreur lors du chargement du questionnaire:', error);
    } finally {
      setLoading(false);
    }
  }, [id, isAuthenticated, user, loadProgress]);

  useEffect(() => {
    initializeQuestionnaire();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [initializeQuestionnaire]);

  useEffect(() => {
    if (!loading) {
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 200);
    }
  }, [currentPage, loading]);

  const saveProgress = useCallback(async () => {
    if (authLoading) return; // Don't save if auth status is not determined yet

    const currentUserId = isAuthenticated ? user.id : anonymousId;
    if (!currentUserId) {
        console.log("No user ID available, cannot save progress.");
        return; // No user ID yet, so don't save.
    }

    const totalQuestions = questions.length;
    const answeredQuestions = Object.keys(answers).length;

    setSaving(true);
    try {
      const apiUrl =
        process.env.NODE_ENV === 'development'
          ? 'http://localhost:7071/api/saveProgress'
          : '/api/saveProgress';

      await axios.post(apiUrl, {
        userId: currentUserId,
        questionnaireId: id,
        questionnaireType: questionnaireConfig?.type,
        userName: isAuthenticated ? user.name : 'Anonyme',
        progress: {
          currentPage,
          answers,
          totalQuestions: totalQuestions,
          answeredQuestions: answeredQuestions,
        },
      }, {
        withCredentials: true,
      });
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    } finally {
      setSaving(false);
    }
  }, [
    currentPage, 
    answers, 
    questions, 
    id, 
    questionnaireConfig, 
    isAuthenticated, 
    user, 
    authLoading,
    anonymousId
  ]);

  useEffect(() => {
    const interval = setInterval(() => {
      saveProgress();
    }, 5000); // Save every 5 seconds

    return () => clearInterval(interval);
  }, [saveProgress]);

  const handleAnswerChange = (questionIndex, value) => {
    const globalIndex = currentPage * QUESTIONS_PER_PAGE + questionIndex;
    const parsedValue = parseInt(value);
    
    setAnswers((prev) => ({
      ...prev,
      [globalIndex]: parsedValue,
    }));

    // Call parent component's handler if provided
    if (onQuestionChange) {
      onQuestionChange(globalIndex, parsedValue, questions[globalIndex]);
    }

    // Auto-scroll to next question after answering
    setTimeout(() => {
      const nextQuestionIndex = questionIndex + 1;
      if (nextQuestionIndex < currentQuestions.length) {
        const nextQuestionElement = document.getElementById(`question-${currentPage * QUESTIONS_PER_PAGE + nextQuestionIndex}`);
        if (nextQuestionElement) {
          nextQuestionElement.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
          });
        }
      }
    }, 300);
  };

  const handleNextPage = async () => {
    // Check if current page is complete
    if (!isCurrentPageComplete()) {
      // Find first unanswered question and scroll to it
      for (let i = 0; i < currentQuestions.length; i++) {
        const globalIndex = currentPage * QUESTIONS_PER_PAGE + i;
        if (answers[globalIndex] === undefined) {
          setTimeout(() => {
            const questionElement = document.getElementById(`question-${globalIndex}`);
            if (questionElement) {
              questionElement.scrollIntoView({ 
                behavior: 'smooth',
                block: 'center',
                inline: 'nearest'
              });
              questionElement.style.border = '2px solid #e74c3c';
              questionElement.style.borderRadius = '8px';
              setTimeout(() => {
                questionElement.style.border = '';
                questionElement.style.borderRadius = '';
              }, 3000);
            }
          }, 100);
          return;
        }
      }
    }

    await saveProgress();

    if (currentPage < totalPages - 1) {
      setCurrentPage((prev) => prev + 1);
      if (onNavigate) {
        onNavigate('next', currentPage + 1);
      }
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      navigate(`/results/${id}`, {
        state: {
          answers,
          questions,
          questionnaireId: id,
          questionnaireType: questionnaireConfig?.type,
        },
      });
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage((prev) => prev - 1);
      if (onNavigate) {
        onNavigate('previous', currentPage - 1);
      }
    }
  };

  const isCurrentPageComplete = () => {
    return currentQuestions.every((_, index) => {
      const globalIndex = currentPage * QUESTIONS_PER_PAGE + index;
      return answers[globalIndex] !== undefined;
    });
  };

  if (authLoading) {
    return <div>Chargement de l'authentification...</div>;
  }

  if (loading) {
    return (
      <div className="container" style={{ marginTop: '2rem' }}>
        <div className="loading">Chargement du questionnaire...</div>
      </div>
    );
  }

  if (!questionnaireConfig) {
    return (
      <div className="container" style={{ marginTop: '2rem' }}>
        <div className="error">Configuration du questionnaire introuvable.</div>
      </div>
    );
  }

  const questionnairesProps = {
    questionnaireConfig,
    questions,
    currentQuestions,
    currentPage,
    totalPages,
    answers,
    progress,
    saving,
    isAuthenticated,
    handleAnswerChange,
    handleNextPage,
    handlePreviousPage,
    isCurrentPageComplete,
    QUESTIONS_PER_PAGE,
  };

  return (
    <div className="container" style={{ marginTop: '2rem' }}>
      {/* Progress Header */}
      <div className="questionnaire-progress">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <div>
            <h1 style={{ color: '#2c3e50', margin: 0 }}>{questionnaireConfig.name}</h1>
            <div style={{ 
              display: 'inline-block',
              backgroundColor: questionnaireConfig.color,
              color: 'white',
              padding: '5px 15px',
              borderRadius: '15px',
              fontSize: '0.8rem',
              fontWeight: 'bold',
              marginTop: '0.5rem'
            }}>
              {questionnaireConfig.icon} {questionnaireConfig.description}
            </div>
          </div>
          <span style={{ color: '#7f8c8d' }}>
            Page {currentPage + 1} sur {totalPages}
          </span>
        </div>
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${progress}%` }}
          ></div>
        </div>
        <div style={{ textAlign: 'center', marginTop: '0.5rem', color: '#7f8c8d' }}>
          {Object.keys(answers).length} / {questions.length} questions répondues ({questionnaireConfig.duration})
        </div>
      </div>

      {saving && (
        <div className="success">
          Sauvegarde en cours...
        </div>
      )}

      {/* Questionnaire Content */}
      {children(questionnairesProps)}

      {/* Navigation */}
      <div className="navigation-buttons">
        <button
          onClick={handlePreviousPage}
          disabled={currentPage === 0}
          className="btn btn-secondary"
        >
          ← Page Précédente
        </button>
        
        <div style={{ textAlign: 'center' }}>
          {!isAuthenticated && (
            <p style={{ color: '#e74c3c', fontSize: '0.9rem', margin: '0.5rem 0' }}>
              💡 Connectez-vous pour sauvegarder votre progression
            </p>
          )}
        </div>

        <button
          onClick={handleNextPage}
          className="btn btn-primary"
        >
          {currentPage === totalPages - 1 ? 'Terminer →' : 'Page Suivante →'}
        </button>
      </div>
    </div>
  );
};

export default BaseQuestionnaire;